import { camelCase } from 'lodash';
import dynamic from 'next/dynamic';

import type { CalloutContentContent } from '@/components/CalloutContent/CalloutContent';
import type { ComparisonTableContainerContent } from '@/components/ComparisonTable/Container/ComparisonTableContainer';
import type { ComparisonTableContent } from '@/components/ComparisonTable/Table/ComparisonTable';
import type { EditorialHeadingParagraphContent } from '@/components/EditorialHeadingParagraph/EditorialHeadingParagraph';
import type { EmbeddedFormContent } from '@/components/EmbeddedForm';
import type { FAQInfoAccordionContent } from '@/components/FAQInfoAccordion/FAQInfoAccordion';
import type { FullImageTwoThirdsColRichTextContent } from '@/components/FullImageTwoThirdsColRichText/FullImageTwoThirdsColRichText';
import type { FullWidthVideoContent } from '@/components/FullWidthVideo/FullWidthVideo';
import type { HeroImageTextFiftyFiftyContent } from '@/components/HeroImageTextFiftyFifty/HeroImageTextFiftyFifty';
import type { HeroMidFullImageContent } from '@/components/HeroMidFullImage/HeroMidFullImage';
import type { HeroOneHalfImageContent } from '@/components/HeroOneHalfImage/HeroOneHalfImage';
import type { HeroSmallTwoThirdsImageContent } from '@/components/HeroSmallTwoThirdsImage/HeroSmallTwoThirdsImage';
import type { HeroTwoThirdsImageContent } from '@/components/HeroTwoThirdsImage/HeroTwoThirdsImage';
import type { HighlightCardsContent } from '@/components/HighlightCards/HighlightCards';
import type { HighlightIconStatContent } from '@/components/HighlightIconStat/HighlightIconStat';
import type { HighlightImageTextStackedContent } from '@/components/HighlightImageTextStacked/HighlightImageTextStacked';
import type { HighlightLightboxContent } from '@/components/HighlightLightbox/HighlightLightbox';
import type { HighlightImageTextContent } from '@/components/HighlightMediaText/HighlightMediaText';
import type { HighlightTextContent } from '@/components/HighlightText/HighlightText';
import type { HighlightVideoInlineContent } from '@/components/HighlightVideoInline/HighlightVideoInline';
import type { ImageGrid3ColWithDescriptionContent } from '@/components/ImageGrid3ColWithDescription/ImageGrid3ColWithDescription';
import type { ImageGrid4ColWithDescriptionContent } from '@/components/ImageGrid4ColWithDescription/ImageGrid4ColWithDescription';
import type { ImageGridFourCirclesTextContent } from '@/components/ImageGridFourCirclesText/ImageGridFourCirclesText';
import type { ImageSectionContent } from '@/components/ImageSection/ImageSection';
import type { InteractiveMapContent } from '@/components/InteractiveMap/InteractiveMap';
import type { InteractiveQuoteFormContent } from '@/components/InteractiveQuoteForm/InteractiveQuoteForm';
import type { LeadFormSectionContent } from '@/components/LeadFormSection/LeadFormSection';
import type { OneHalfImageContent } from '@/components/OneHalfImage/OneHalfImage';
import type { OneThirdImageWithCopyContent } from '@/components/OneThirdImageWithCopy/OneThirdImageWithCopy';
import ProductCatalogList from '@/components/ProductCatalogList/ProductCatalogList';
import type { RichTextContent } from '@/components/RichText/RichTextSection';
import type { StateSolarIncentivesContent } from '@/components/StateSolarIncentives/StateSolarIncentives';
import type { SwitchableContentSelectorContent } from '@/components/SwitchableContentSelector/SwitchableContentSelector';
import type { TaxFormContent } from '@/components/TaxForm/TaxForm';
import type { TaxFormSectionContent } from '@/components/TaxFormSection/TaxFormSection';
import type { ActionBannerFullContent } from '@/components/TopLevelComponents/ActionBannerFull/ActionBannerFull';
import type { ActionCardsContent } from '@/components/TopLevelComponents/ActionCards/ActionCardsWrapper';
import type { ActionUserInputContent } from '@/components/TopLevelComponents/ActionUserInput/ActionUserInput';
import type { ActionUserInputHorizontalContent } from '@/components/TopLevelComponents/ActionUserInputHorizontal/ActionUserInputHorizontal';
import type { GlobalFAQContent } from '@/components/TopLevelComponents/GlobalFAQ/GlobalFAQ';
import type { GlobalListContent } from '@/components/TopLevelComponents/GlobalList/GlobalList';
import type { HeroFullOverlayContent } from '@/components/TopLevelComponents/HeroFullOverlay/HeroFullOverlay';
import type { HighlightTestimonialContent } from '@/components/TopLevelComponents/HighlightTestimonial/HighlightTestimonial';
import type { PaidLanderContent } from '@/components/TopLevelComponents/PaidLander/PaidLander';
import type { WayfindingContent } from '@/components/TopLevelComponents/Wayfinding/Wayfinding';
import type { ProductGridAdapted } from '@/contentful/api/sunrunShopTypes';
import type {
  ActionDropdownCF,
  EcommIntakeCF,
  EmbeddedImageCf,
  HeroCarouselCF,
  HeroLeadFormCF,
  HighlightContactCardsCf,
} from '@/types';

const ActionBannerFull = dynamic(
  () => import('../TopLevelComponents/ActionBannerFull/ActionBannerFull')
);
const ActionCardsWrapper = dynamic(
  () => import('../TopLevelComponents/ActionCards/ActionCardsWrapper')
);
const ActionUserInput = dynamic(
  () => import('../TopLevelComponents/ActionUserInput/ActionUserInput')
);
const ActionUserInputHorizontal = dynamic(
  () =>
    import(
      '../TopLevelComponents/ActionUserInputHorizontal/ActionUserInputHorizontal'
    )
);
const CalloutContent = dynamic(
  () => import('../CalloutContent/CalloutContent')
);
const ComparisonTable = dynamic(
  () => import('../ComparisonTable/Table/ComparisonTable')
);
const ComparisonTableContainer = dynamic(
  () => import('../ComparisonTable/Container/ComparisonTableContainer')
);
const EditorialHeadingParagraph = dynamic(
  () => import('../EditorialHeadingParagraph/EditorialHeadingParagraph')
);
const EmbeddedForm = dynamic(() => import('../EmbeddedForm'));
const EmbeddedImage = dynamic(() => import('../EmbeddedImage/EmbeddedImage'));
const FAQInfoAccordion = dynamic(
  () => import('../FAQInfoAccordion/FAQInfoAccordion')
);
const FullImageTwoThirdsColRichText = dynamic(
  () => import('../FullImageTwoThirdsColRichText/FullImageTwoThirdsColRichText')
);
const FullWidthVideo = dynamic(
  () => import('../FullWidthVideo/FullWidthVideo')
);
const GlobalFAQ = dynamic(
  () => import('../TopLevelComponents/GlobalFAQ/GlobalFAQ')
);
const GlobalList = dynamic(
  () => import('../TopLevelComponents/GlobalList/GlobalList')
);
const HeroCarousel = dynamic(() => import('../HeroCarousel/HeroCarousel'));
const HeroFullOverlay = dynamic(
  () => import('../TopLevelComponents/HeroFullOverlay/HeroFullOverlay')
);
const HeroImageTextFiftyFifty = dynamic(
  () => import('../HeroImageTextFiftyFifty/HeroImageTextFiftyFifty')
);
const HeroMidFullImage = dynamic(
  () => import('../HeroMidFullImage/HeroMidFullImage')
);
const HeroOneHalfImage = dynamic(
  () => import('../HeroOneHalfImage/HeroOneHalfImage')
);
const HeroSmallTwoThirdsImage = dynamic(
  () => import('../HeroSmallTwoThirdsImage/HeroSmallTwoThirdsImage')
);
const HeroTwoThirdsImage = dynamic(
  () => import('../HeroTwoThirdsImage/HeroTwoThirdsImage')
);
const HighlightCards = dynamic(
  () => import('../HighlightCards/HighlightCards')
);
const HighlightContactCards = dynamic(
  () => import('../HighlightContactCards/HighlightContactCards')
);
const HighlightIconStat = dynamic(
  () => import('../HighlightIconStat/HighlightIconStat')
);
const HighlightImageTextStacked = dynamic(
  () => import('../HighlightImageTextStacked/HighlightImageTextStacked')
);
const HighlightLightbox = dynamic(
  () => import('../HighlightLightbox/HighlightLightbox')
);
const HighlightMediaText = dynamic(
  () => import('../HighlightMediaText/HighlightMediaText')
);
const HighlightTestimonial = dynamic(
  () =>
    import('../TopLevelComponents/HighlightTestimonial/HighlightTestimonial')
);
const HighlightText = dynamic(() => import('../HighlightText/HighlightText'));
const HighlightVideoInline = dynamic(
  () => import('../HighlightVideoInline/HighlightVideoInline')
);
const ImageGrid3ColWithDescription = dynamic(
  () => import('../ImageGrid3ColWithDescription/ImageGrid3ColWithDescription')
);
const ImageGrid4ColWithDescription = dynamic(
  () => import('../ImageGrid4ColWithDescription/ImageGrid4ColWithDescription')
);
const ImageGridFourCirclesText = dynamic(
  () => import('../ImageGridFourCirclesText/ImageGridFourCirclesText')
);
const ImageSection = dynamic(() => import('../ImageSection/ImageSection'));
const InteractiveMapSection = dynamic(
  () => import('../InteractiveMap/InteractiveMap')
);
const InteractiveQuoteForm = dynamic(
  () => import('../InteractiveQuoteForm/InteractiveQuoteForm')
);
const LeadFormSection = dynamic(
  () => import('../LeadFormSection/LeadFormSection')
);
const OneHalfImage = dynamic(() => import('../OneHalfImage/OneHalfImage'));
const OneThirdImageWithCopy = dynamic(
  () => import('../OneThirdImageWithCopy/OneThirdImageWithCopy')
);
const PaidLander = dynamic(
  () => import('../TopLevelComponents/PaidLander/PaidLander')
);
const ReferralForm = dynamic(() => import('../ReferralForm/ReferralForm'));
const RichTextSection = dynamic(() => import('../RichText/RichTextSection'));
const StateSolarIncentives = dynamic(
  () => import('../StateSolarIncentives/StateSolarIncentives')
);
const SwitchableContentSelector = dynamic(
  () => import('../SwitchableContentSelector/SwitchableContentSelector')
);
const TaxFormSection = dynamic(
  () => import('../TaxFormSection/TaxFormSection')
);
const Wayfinding = dynamic(
  () => import('../TopLevelComponents/Wayfinding/Wayfinding')
);
const ActionDropdown = dynamic(
  () => import('../ActionDropdown/ActionDropdown')
);
const HeroLeadForm = dynamic(() => import('../HeroLeadForm/HeroLeadForm'));
const EcommIntake = dynamic(() => import('../EcommIntake/EcommIntake'));

// Type for all different content sections rendered by application
export type SectionContent =
  | ActionUserInputHorizontalContent
  | CalloutContentContent
  | ComparisonTableContainerContent
  | ComparisonTableContent
  | EditorialHeadingParagraphContent
  | EmbeddedFormContent
  | EmbeddedImageCf
  | FAQInfoAccordionContent
  | FullImageTwoThirdsColRichTextContent
  | FullWidthVideoContent
  | HeroCarouselCF
  | HeroMidFullImageContent
  | HeroOneHalfImageContent
  | HeroSmallTwoThirdsImageContent
  | HeroTwoThirdsImageContent
  | HighlightIconStatContent
  | HighlightImageTextContent
  | HighlightImageTextStackedContent
  | HighlightVideoInlineContent
  | HighlightLightboxContent
  | HighlightTextContent
  | HighlightContactCardsCf
  | ImageGrid4ColWithDescriptionContent
  | ImageGridFourCirclesTextContent
  | ImageSectionContent
  | InteractiveMapContent
  | LeadFormSectionContent
  | OneHalfImageContent
  | OneThirdImageWithCopyContent
  | PaidLanderContent
  | RichTextContent
  | StateSolarIncentivesContent
  | StateSolarIncentivesContent
  | TaxFormContent
  | TaxFormSectionContent
  | WayfindingContent
  | ActionDropdownCF
  | HeroLeadFormCF
  | EcommIntakeCF
  | ProductGridAdapted;

interface Props {
  content: SectionContent;
  isFeatured?: boolean;
  sectionPosition?: number;
  componentPosition?: number;
}

// Maps all content types to react components by ID
const GenericComponent = ({
  content,
  isFeatured,
  sectionPosition,
  componentPosition,
}: Props): React.JSX.Element | null => {
  const sectionComponentId = content?.sys?.contentType?.sys?.id;
  const standarizeId = sectionComponentId ? camelCase(sectionComponentId) : '';

  switch (standarizeId) {
    case 'leadFormContainer':
      return <LeadFormSection content={content as LeadFormSectionContent} />;
    case 'heroMidFullImage':
      return <HeroMidFullImage content={content as HeroMidFullImageContent} />;
    case 'heroOneHalfImage':
      return <HeroOneHalfImage content={content as HeroOneHalfImageContent} />;
    case 'oneHalfImage':
      return <OneHalfImage content={content as OneHalfImageContent} />;
    case 'imageGrid4ColWithDescription':
      return (
        <ImageGrid4ColWithDescription
          content={content as ImageGrid4ColWithDescriptionContent}
        />
      );
    case 'calloutContent':
      return <CalloutContent content={content as CalloutContentContent} />;
    case 'heroTwoThirdsImage':
      return (
        <HeroTwoThirdsImage content={content as HeroTwoThirdsImageContent} />
      );
    case 'interactiveMap':
      return (
        <InteractiveMapSection content={content as InteractiveMapContent} />
      );
    case 'fullImageTwoThirdsColRichText':
      return (
        <FullImageTwoThirdsColRichText
          content={content as FullImageTwoThirdsColRichTextContent}
        />
      );
    case 'oneThirdImageWithCopy':
      return (
        <OneThirdImageWithCopy
          content={content as OneThirdImageWithCopyContent}
        />
      );
    case 'fullWidthVideo':
      return <FullWidthVideo content={content as FullWidthVideoContent} />;
    case 'imageGrid3ColWithDescription':
      return (
        <ImageGrid3ColWithDescription
          content={content as ImageGrid3ColWithDescriptionContent}
        />
      );
    case 'comparisonTableContainer':
      return (
        <ComparisonTableContainer
          content={content as ComparisonTableContainerContent}
        />
      );
    case 'comparisonTable':
      return <ComparisonTable content={content as ComparisonTableContent} />;
    case 'heroSmallTwoThirdsImage':
      return (
        <HeroSmallTwoThirdsImage
          content={content as HeroSmallTwoThirdsImageContent}
        />
      );
    case 'imageSection':
      return <ImageSection content={content as ImageSectionContent} />;
    case 'stateSolarIncentives':
      return (
        <StateSolarIncentives
          content={content as StateSolarIncentivesContent}
        />
      );
    case 'richText':
      return <RichTextSection content={content as RichTextContent} />;
    case 'faqinfoAccordion':
      return <FAQInfoAccordion content={content as FAQInfoAccordionContent} />;
    case 'switchableContentSelector':
      return (
        <SwitchableContentSelector
          content={content as SwitchableContentSelectorContent}
        />
      );
    case 'imageGridFourCirclesText': {
      return (
        <ImageGridFourCirclesText
          content={content as ImageGridFourCirclesTextContent}
        />
      );
    }
    case 'taxFormSection': {
      return <TaxFormSection content={content as TaxFormSectionContent} />;
    }
    case 'referralForm': {
      return <ReferralForm />;
    }
    case 'interactiveQuoteForm': {
      return (
        <InteractiveQuoteForm
          content={content as InteractiveQuoteFormContent}
        />
      );
    }
    case 'embeddedForm': {
      return <EmbeddedForm content={content as EmbeddedFormContent} />;
    }
    case 'embeddedImage': {
      return <EmbeddedImage content={content as EmbeddedImageCf} />;
    }
    case 'heroFullOverlay': {
      return <HeroFullOverlay content={content as HeroFullOverlayContent} />;
    }
    case 'actionBannerFull': {
      return <ActionBannerFull content={content as ActionBannerFullContent} />;
    }
    case 'heroImageTextFiftyFifty': {
      return (
        <HeroImageTextFiftyFifty
          content={content as HeroImageTextFiftyFiftyContent}
          isFeatured={false}
        />
      );
    }
    case 'highlightImageTextStacked': {
      return (
        <HighlightImageTextStacked
          content={content as HighlightImageTextStackedContent}
          isFeatured={isFeatured}
        />
      );
    }
    case 'highlightVideoInline': {
      return (
        <HighlightVideoInline
          content={content as HighlightImageTextStackedContent}
        />
      );
    }
    case 'highlightImageText': {
      return (
        <HighlightMediaText
          content={content as HighlightImageTextContent}
          isFeatured={isFeatured}
        />
      );
    }
    case 'highlightIconTextStacked': {
      return (
        <HighlightIconStat content={content as HighlightIconStatContent} />
      );
    }
    case 'highlightLightbox': {
      return (
        <HighlightLightbox content={content as HighlightLightboxContent} />
      );
    }
    case 'highlightContactCards': {
      return (
        <HighlightContactCards
          content={content as HighlightContactCardsCf}
          isFeatured={isFeatured}
        />
      );
    }
    case 'actionUserInput': {
      return (
        <ActionUserInput
          content={content as ActionUserInputContent}
          sectionPosition={sectionPosition}
          componentPosition={componentPosition}
        />
      );
    }
    case 'actionCards': {
      return <ActionCardsWrapper content={content as ActionCardsContent} />;
    }
    case 'wayfinding': {
      return <Wayfinding content={content as WayfindingContent} />;
    }
    case 'highlightText': {
      return (
        <HighlightText
          content={content as HighlightTextContent}
          isFeatured={isFeatured}
        />
      );
    }
    case 'globalFaq': {
      return <GlobalFAQ content={content as GlobalFAQContent} />;
    }
    case 'highlightTestimonial': {
      return (
        <HighlightTestimonial
          content={content as HighlightTestimonialContent}
        />
      );
    }
    case 'globalList': {
      return <GlobalList content={content as GlobalListContent} />;
    }
    case 'editorialHeadingParagraph': {
      return (
        <EditorialHeadingParagraph
          content={content as EditorialHeadingParagraphContent}
        />
      );
    }
    case 'highlightCardsComponent': {
      return <HighlightCards content={content as HighlightCardsContent} />;
    }
    case 'actionUserInputHorizontal': {
      return (
        <ActionUserInputHorizontal
          content={content as ActionUserInputHorizontalContent}
        />
      );
    }
    case 'paidLander': {
      return (
        <PaidLander
          content={content as PaidLanderContent}
          showQualify={true}
          showSubmit={false}
        />
      );
    }
    case 'actionDropdown': {
      return <ActionDropdown content={content as ActionDropdownCF} />;
    }
    case 'heroLeadForm': {
      return <HeroLeadForm content={content as HeroLeadFormCF} />;
    }
    case 'heroCarousel': {
      return <HeroCarousel content={content as HeroCarouselCF} />;
    }
    case 'ecommIntake': {
      return <EcommIntake content={content as EcommIntakeCF} />;
    }
    case 'sunrunShopProductGrid': {
      return <ProductCatalogList content={content as ProductGridAdapted} />;
    }

    default:
      return null;
  }
};

export default GenericComponent;
